import { useState, MouseEvent } from 'react';
import { makeStyles } from '@material-ui/styles';
import type { ReferenceObject } from 'popper.js';
import { Popper, PopperProps, ClickAwayListener } from '@material-ui/core';
import cn from 'clsx';

import { apiClasses } from './definitions';
import Fade from './Fade';
import { BaseButton } from '../../buttons';

type OnClickHandler = (event: MouseEvent<Document | Element>) => void;

type DropdownPropsType = {
    button: ({ isOpen }) => React.ReactNode;
    onOpen?: OnClickHandler;
    onClose?: OnClickHandler;
    isOpen?: boolean;
    container?: HTMLElement | string;
    clickAwayMouseEvent?: 'onClick' | 'onMouseDown' | 'onMouseUp' | false;
    clickAwayTouchEvent?: 'onTouchEnd' | 'onTouchStart' | false;
    flipEnabled?: boolean;
    disabled?: boolean;
    dropDownButtonClassname?: string;
};

type PopperPropsWithoutChildren = Omit<Partial<PopperProps>, 'children'>;

const Dropdown: React.FC<PopperPropsWithoutChildren & DropdownPropsType> = ({
    placement = 'bottom-start',
    isOpen: initialIsOpen = false,
    button,
    children,
    onOpen = () => {},
    onClose = () => {},
    container = 'window',
    clickAwayMouseEvent = 'onClick',
    clickAwayTouchEvent = 'onTouchEnd',
    flipEnabled = true,
    disabled = false,
    dropDownButtonClassname,
}) => {
    const [isOpen, setIsOpen] = useState(initialIsOpen);
    const [anchorEl, setAnchorEl] = useState<PopperProps['anchorEl']>(null);

    const closeDropdown: OnClickHandler = event => {
        onClose(event);
        setIsOpen(false);
        setAnchorEl(null);
    };
    const openDropdown: OnClickHandler = event => {
        if (disabled) {
            return;
        }
        onOpen(event);
        setIsOpen(true);
        setAnchorEl(event.currentTarget as ReferenceObject);
    };

    const classes = useStyles();

    return (
        <ClickAwayListener
            onClickAway={closeDropdown}
            mouseEvent={clickAwayMouseEvent}
            touchEvent={clickAwayTouchEvent}
        >
            <div className={apiClasses.dropdown}>
                <BaseButton
                    className={cn(
                        apiClasses.dropdownButton,
                        classes.dropdownButton,
                        dropDownButtonClassname
                    )}
                    onClick={isOpen ? closeDropdown : openDropdown}
                    aria-expanded={isOpen}
                    data-testid="dropdown-trigger"
                >
                    {button({ isOpen })}
                </BaseButton>
                <Popper
                    open={isOpen}
                    anchorEl={anchorEl}
                    placement={placement}
                    modifiers={{
                        preventOverflow: {
                            enabled: true,
                            boundariesElement: container,
                        },
                        flip: {
                            enabled: flipEnabled,
                        },
                    }}
                    className={classes.tooltip}
                    transition
                >
                    {({ TransitionProps }) => (
                        <Fade {...TransitionProps}>
                            <div className={`${classes.content} ${apiClasses.dropdownContent}`}>
                                {typeof children === 'function'
                                    ? children(closeDropdown)
                                    : children}
                            </div>
                        </Fade>
                    )}
                </Popper>
            </div>
        </ClickAwayListener>
    );
};

const useStyles = makeStyles(
    {
        dropdownButton: {
            display: 'block',
        },
        tooltip: {
            zIndex: 1001,
        },
        content: {
            maxWidth: 'calc(100vw - 24px)',
            margin: '8px 0',
        },
    },
    { classNamePrefix: 'drop-down' }
);

export default Dropdown;
